import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, ChevronLeft, Check, X, ShieldCheck, MessageCircle, Loader2, MapPin, CalendarClock } from "lucide-react";
import { euro } from "@/lib/store";
import { valuationConfig, valuationEstimate, valuationSubmit } from "@/lib/valuation";
import { Seo } from "@/components/Seo";

const YES = "yes", NO = "no", UNKNOWN = "unknown";

const Option = ({ active, onClick, children, testid, tone = "default" }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left text-base font-semibold transition-all duration-200 active:scale-[0.98] ${
      active
        ? "border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/20"
        : tone === "danger"
        ? "border-slate-200 bg-white text-slate-800 hover:border-rose-300 hover:bg-rose-50/40"
        : "border-slate-200 bg-white text-slate-800 hover:border-slate-900 hover:bg-slate-50"
    }`}
  >
    <span>{children}</span>
    {active && <Check className="h-5 w-5 shrink-0" />}
  </button>
);

const StepShell = ({ title, subtitle, children }) => (
  <motion.div
    initial={{ opacity: 0, x: 24 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -24 }}
    transition={{ duration: 0.25 }}
    className="space-y-5"
  >
    <div>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
    </div>
    <div className="space-y-3">{children}</div>
  </motion.div>
);

export default function ValutaIphone() {
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState(0);
  const [modelId, setModelId] = useState(null);
  const [storage, setStorage] = useState(null);
  const [a, setA] = useState({}); // answers
  const [result, setResult] = useState(null);
  const [phase, setPhase] = useState("wizard"); // wizard | estimate | form | done | notEligible | unavailable
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", requested_date: "", requested_time: "", notes: "" });
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => { valuationConfig().then(setCfg).catch(() => {}); window.scrollTo(0, 0); }, []);

  const model = cfg?.models?.find((m) => m.id === modelId);
  const storages = model?.storages || [];

  const TOTAL_STEPS = 10;
  const progress = Math.min(100, Math.round(((phase === "wizard" ? step : TOTAL_STEPS) / TOTAL_STEPS) * 100));

  const compute = async (answers) => {
    setLoading(true);
    try {
      const res = await valuationEstimate({ model_id: modelId, storage, answers });
      setResult(res);
      if (!res.eligible) setPhase("notEligible");
      else if (res.available === false) setPhase("unavailable");
      else setPhase("estimate");
    } catch (e) {
      setPhase("unavailable");
    } finally { setLoading(false); }
  };

  // avanza; se answer bloccante -> calcola subito
  const next = (patch = {}, blocking = false) => {
    const answers = { ...a, ...patch };
    setA(answers);
    if (blocking) { compute(answers); return; }
    if (step >= TOTAL_STEPS - 1) { compute(answers); return; }
    setStep(step + 1);
  };

  const back = () => {
    if (phase !== "wizard") { setPhase("wizard"); return; }
    if (step === 0) return;
    setStep(step - 1);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await valuationSubmit({ model_id: modelId, storage, answers: a, ...form });
      setSubmitted(res);
      setPhase("done");
      window.scrollTo(0, 0);
    } catch (e) {
      alert("Errore nell'invio. Riprova o contattaci su WhatsApp.");
    } finally { setLoading(false); }
  };

  if (!cfg) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Seo route="/valuta-iphone" />
      {/* Hero SEO */}
      <section className="border-b border-slate-100 bg-white px-4 pb-6 pt-10 sm:pt-14">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
            <Smartphone className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Valuta il tuo iPhone</h1>
          <p className="mx-auto mt-3 max-w-lg text-sm text-slate-500 sm:text-base">
            Scopri quanto può valere il tuo iPhone in meno di 90 secondi. Ricevi una <strong className="text-slate-700">stima preliminare</strong> e prenota la verifica in negozio.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-2xl px-4 py-8">
        {/* Progress */}
        {phase === "wizard" && (
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-400">
              <span>Passo {step + 1} di {TOTAL_STEPS}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <motion.div className="h-full rounded-full bg-slate-900" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
            </div>
          </div>
        )}

        {/* Back */}
        {(phase === "wizard" ? step > 0 : phase !== "done") && (
          <button data-testid="valuation-back-btn" onClick={back} className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900">
            <ChevronLeft className="h-4 w-4" /> Indietro
          </button>
        )}

        {loading && <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>}

        {!loading && (
        <AnimatePresence mode="wait">
          {phase === "wizard" && step === 0 && (
            <StepShell key="s0" title="Quale iPhone vuoi vendere?" subtitle="Seleziona il modello del tuo dispositivo.">
              <div className="grid grid-cols-2 gap-3">
                {cfg.models.map((m) => (
                  <button key={m.id} data-testid={`model-${m.id}`} onClick={() => { setModelId(m.id); setStorage(null); setStep(1); }}
                    className={`rounded-2xl border px-4 py-4 text-left text-sm font-semibold transition-all active:scale-[0.98] ${modelId === m.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-slate-900"}`}>
                    {m.model}
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {phase === "wizard" && step === 1 && (
            <StepShell key="s1" title="Quanta memoria ha?" subtitle={model?.model}>
              <div className="grid grid-cols-2 gap-3">
                {storages.map((s) => (
                  <button key={s} data-testid={`storage-${s.replace(/\s/g, "")}`} onClick={() => { setStorage(s); setStep(2); }}
                    className={`rounded-2xl border px-4 py-5 text-center text-lg font-bold transition-all active:scale-[0.98] ${storage === s ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-slate-900"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {phase === "wizard" && step === 2 && (
            <StepShell key="s2" title="Il tuo iPhone funziona perfettamente?">
              <Option testid="q-functional-yes" onClick={() => next({ functional_ok: true })}>Sì, funziona perfettamente</Option>
              <Option testid="q-functional-no" tone="danger" onClick={() => next({ functional_ok: false }, true)}>No, ha qualche problema</Option>
            </StepShell>
          )}

          {phase === "wizard" && step === 3 && (
            <StepShell key="s3" title="Il display è integro?" subtitle="Nessuna crepa, macchia o pixel difettosi.">
              <Option testid="q-display-yes" onClick={() => next({ display_intact: true })}>Sì, è integro</Option>
              <Option testid="q-display-no" tone="danger" onClick={() => next({ display_intact: false }, true)}>No</Option>
            </StepShell>
          )}

          {phase === "wizard" && step === 4 && (
            <StepShell key="s4" title="Il display è originale e non è mai stato sostituito?">
              <Option testid="q-disporig-yes" onClick={() => next({ display_original: YES })}>Sì, è originale</Option>
              <Option testid="q-disporig-no" tone="danger" onClick={() => next({ display_original: NO }, true)}>No / Non so</Option>
            </StepShell>
          )}

          {phase === "wizard" && step === 5 && (
            <StepShell key="s5" title="Il telefono è mai stato riparato?" subtitle="Accettiamo solo dispositivi mai riparati.">
              <Option testid="q-repaired-no" onClick={() => next({ repaired: NO })}>No, mai riparato</Option>
              <Option testid="q-repaired-yes" tone="danger" onClick={() => next({ repaired: YES }, true)}>Sì, è stato riparato</Option>
              <Option testid="q-repaired-unknown" tone="danger" onClick={() => next({ repaired: UNKNOWN }, true)}>Non so</Option>
            </StepShell>
          )}

          {phase === "wizard" && step === 6 && (
            <StepShell key="s6" title="Come sono le condizioni estetiche?">
              {cfg.aesthetic_grades.map((g) => (
                <Option key={g.code} testid={`q-aesthetic-${g.code}`} active={a.aesthetic === g.code} onClick={() => next({ aesthetic: g.code })}>{g.label}</Option>
              ))}
            </StepShell>
          )}

          {phase === "wizard" && step === 7 && (
            <StepShell key="s7" title="Qual è la salute della batteria?" subtitle="La trovi in Impostazioni › Batteria › Stato batteria.">
              {cfg.battery_bands.map((b) => (
                <Option key={b.code} testid={`q-battery-${b.code}`} active={a.battery_band === b.code} onClick={() => next({ battery_band: b.code })}>{b.label}</Option>
              ))}
            </StepShell>
          )}

          {phase === "wizard" && step === 8 && (
            <StepShell key="s8" title="La batteria è originale?">
              <Option testid="q-battorig-yes" onClick={() => next({ battery_original: YES })}>Sì</Option>
              <Option testid="q-battorig-no" onClick={() => next({ battery_original: NO })}>No / Non so</Option>
            </StepShell>
          )}

          {phase === "wizard" && step === 9 && (
            <StepShell key="s9" title="Il dispositivo presenta uno di questi problemi?" subtitle="Face ID, fotocamere, connettore di ricarica, microfono, altoparlanti, Wi-Fi, Bluetooth, NFC, danni da liquido, scocca piegata, IMEI, blocco attivazione o MDM.">
              <Option testid="q-checks-no" onClick={() => next({ iphix_issue: false }, false)}>No, nessun problema</Option>
              <Option testid="q-checks-yes" tone="danger" onClick={() => next({ iphix_issue: true }, true)}>Sì, almeno uno</Option>
            </StepShell>
          )}

          {/* RESULT: eleggibile con stima */}
          {phase === "estimate" && result && (
            <motion.div key="estimate" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="valuation-estimate">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Valutazione preliminare IPHIX</p>
                <p className="mt-2 text-sm text-slate-500">{result.model} · {result.storage}</p>
                <p className="mt-4 text-sm text-slate-500">Il tuo iPhone può essere valutato fino a</p>
                <p className="mt-1 text-5xl font-extrabold tracking-tight text-slate-900" data-testid="estimate-value">{euro(result.estimated_value_cents)}</p>
                <p className="mt-4 text-xs leading-relaxed text-slate-400">
                  La valutazione è preliminare e sarà confermata dopo la verifica fisica del dispositivo presso il nostro punto vendita. Validità {result.validity_days} giorni.
                </p>
              </div>
              <button data-testid="valuation-continue-form" onClick={() => setPhase("form")} className="w-full rounded-full bg-slate-900 py-4 text-base font-semibold text-white transition-all hover:bg-slate-800 active:scale-[0.98]">
                Prenota la verifica in negozio
              </button>
            </motion.div>
          )}

          {/* RESULT: contatto (form) */}
          {phase === "form" && (
            <StepShell key="form" title="Prenota la tua verifica" subtitle="Lascia i tuoi contatti: ti ricontatteremo per confermare l'appuntamento.">
              <input data-testid="form-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nome e cognome *" className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-base outline-none focus:border-slate-900" />
              <input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono / WhatsApp *" className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-base outline-none focus:border-slate-900" />
              <input data-testid="form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (facoltativa)" className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-base outline-none focus:border-slate-900" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="form-date" type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-base outline-none focus:border-slate-900" />
                <select data-testid="form-time" value={form.requested_time} onChange={(e) => setForm({ ...form, requested_time: e.target.value })} className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-base outline-none focus:border-slate-900">
                  <option value="">Fascia oraria</option>
                  <option value="Mattina">Mattina</option>
                  <option value="Pomeriggio">Pomeriggio</option>
                </select>
              </div>
              <textarea data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note (facoltative)" rows={2} className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-base outline-none focus:border-slate-900" />
              <button data-testid="form-submit" disabled={!form.customer_name || !form.phone} onClick={submit} className="w-full rounded-full bg-slate-900 py-4 text-base font-semibold text-white transition-all hover:bg-slate-800 active:scale-[0.98] disabled:opacity-40">
                Invia richiesta
              </button>
              <p className="text-center text-xs text-slate-400">Nessun messaggio automatico verrà inviato. Ti ricontatteremo noi.</p>
            </StepShell>
          )}

          {/* RESULT: non eleggibile */}
          {phase === "notEligible" && result && (
            <motion.div key="ne" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="valuation-not-eligible">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500"><X className="h-6 w-6" /></div>
                <h2 className="text-xl font-bold text-slate-900">Non possiamo valutarlo online</h2>
                <p className="mt-2 text-sm text-slate-500">{result.message}</p>
                {result.labels?.length > 0 && (
                  <ul className="mt-4 space-y-2 text-left text-sm text-slate-600">
                    {result.labels.map((l, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-400" />{l}</li>)}
                  </ul>
                )}
                <p className="mt-5 text-xs text-slate-400">Puoi comunque passare in negozio per una verifica gratuita.</p>
              </div>
              <a data-testid="ne-whatsapp" href={`https://wa.me/3908118913768?text=${encodeURIComponent("Ciao IPHIX, vorrei informazioni sulla valutazione del mio iPhone.")}`} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-base font-semibold text-white transition-all hover:bg-[#20ba5a] active:scale-[0.98]">
                <MessageCircle className="h-5 w-5" /> Contatta IPHIX su WhatsApp
              </a>
            </motion.div>
          )}

          {/* RESULT: online non disponibile per la configurazione */}
          {phase === "unavailable" && (
            <motion.div key="ua" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="valuation-unavailable">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-500"><ShieldCheck className="h-6 w-6" /></div>
                <h2 className="text-xl font-bold text-slate-900">Valutazione non disponibile online</h2>
                <p className="mt-2 text-sm text-slate-500">Per questa configurazione contattaci: ti forniremo una stima personalizzata.</p>
              </div>
              <a href={`https://wa.me/3908118913768?text=${encodeURIComponent("Ciao IPHIX, vorrei una valutazione del mio iPhone.")}`} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-base font-semibold text-white transition-all hover:bg-[#20ba5a] active:scale-[0.98]">
                <MessageCircle className="h-5 w-5" /> Contatta IPHIX su WhatsApp
              </a>
            </motion.div>
          )}

          {/* THANK YOU */}
          {phase === "done" && submitted && (
            <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="valuation-done">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-500"><Check className="h-6 w-6" /></div>
                <h2 className="text-2xl font-bold text-slate-900">Richiesta ricevuta</h2>
                {submitted.eligible ? (
                  <div className="mt-4 space-y-1 text-sm text-slate-600">
                    <p>{submitted.model} · {submitted.storage}</p>
                    <p className="text-2xl font-extrabold text-slate-900">{euro(submitted.estimated_value_cents)}</p>
                    {submitted.expires_at && <p className="text-xs text-slate-400">Valida fino al {new Date(submitted.expires_at).toLocaleDateString("it-IT")}</p>}
                    {submitted.requested_date && <p className="text-xs text-slate-400 flex items-center justify-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Appuntamento richiesto: {submitted.requested_date} {submitted.requested_time}</p>}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">Abbiamo registrato la tua richiesta. Passa in negozio per una verifica gratuita.</p>
                )}
              </div>
              {submitted.whatsapp_url && (
                <a data-testid="done-whatsapp" href={submitted.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-base font-semibold text-white transition-all hover:bg-[#20ba5a] active:scale-[0.98]">
                  <MessageCircle className="h-5 w-5" /> Contatta IPHIX su WhatsApp
                </a>
              )}
              <Link to="/" className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 py-4 text-base font-semibold text-slate-700 transition-all hover:border-slate-900 active:scale-[0.98]">
                <MapPin className="h-4 w-4" /> Torna alla home
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
        )}
      </div>
    </div>
  );
}
